import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Customer, Invitation } from '../types';
import { teamApi } from '../api/team';
import { customerApi } from '../api/machines';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { Toast, useToast } from '../components/Toast';
import './TeamInvitations.css';

const statusBadge: Record<string, string> = {
  PENDING: 'badge badge-warning',
  ACCEPTED: 'badge badge-success',
  EXPIRED: 'badge badge-danger',
};
const statusLabel: Record<string, string> = {
  PENDING: 'Oczekuje',
  ACCEPTED: 'Aktywowane',
  EXPIRED: 'Wygasłe',
};
const roleLabel = (inv: Invitation) =>
  inv.roleType === 'EMPLOYEE_ADMIN' ? 'admin'
    : inv.roleType === 'CUSTOMER' ? `klient${inv.customerNip ? ` (NIP ${inv.customerNip})` : ''}`
    : 'pracownik';

const TeamInvitations: React.FC = () => {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custFirstName, setCustFirstName] = useState('');
  const [custLastName, setCustLastName] = useState('');
  const [custNip, setCustNip] = useState('');
  const [busy, setBusy] = useState(false);
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState<Invitation | null>(null);
  const { toast, showToast, dismissToast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await teamApi.invitations();
      setInvitations(data);
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się pobrać zaproszeń.');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    customerApi.getAll()
      .then(({ data }) => setCustomers(data))
      .catch(() => { /* formularz klienta pokaże pustą listę */ });
  }, []);

  if (!user?.roles?.includes('EMPLOYEE_ADMIN') && !user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await teamApi.invite(email.trim(), firstName.trim(), lastName.trim());
      setEmail(''); setFirstName(''); setLastName('');
      showToast('success', 'Zaproszenie pracownika wysłane.');
      load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się wysłać zaproszenia.');
    } finally {
      setBusy(false);
    }
  };

  const inviteCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await teamApi.inviteCustomer(custEmail.trim(), custNip, custFirstName.trim(), custLastName.trim());
      setCustEmail(''); setCustFirstName(''); setCustLastName(''); setCustNip('');
      showToast('success', 'Zaproszenie klienta wysłane.');
      load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się wysłać zaproszenia.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async (inv: Invitation) => {
    setRowBusyId(inv.id);
    try {
      await teamApi.resendInvitation(inv.id);
      showToast('success', `Zaproszenie do ${inv.email} wysłane ponownie (nowy link, ważny 7 dni).`);
      load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się ponowić zaproszenia.');
    } finally {
      setRowBusyId(null);
    }
  };

  const doCancel = async () => {
    if (!confirmCancel) return;
    const inv = confirmCancel;
    setRowBusyId(inv.id);
    try {
      await teamApi.cancelInvitation(inv.id);
      setConfirmCancel(null);
      showToast('success', `Zaproszenie do ${inv.email} anulowane.`);
      load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się anulować zaproszenia.');
    } finally {
      setRowBusyId(null);
    }
  };

  const customerLabel = (c: Customer) => (c.companyName ? `${c.companyName} (${c.nip})` : c.nip);

  return (
    <div>
      <h1 className="page-title">Zaproszenia</h1>
      <p className="item-meta" style={{ marginBottom: '1rem' }}>
        Zaproszenia mailowe to jedyna ścieżka zakładania kont — po ustawieniu hasła konto jest od razu aktywne.
      </p>

      <form onSubmit={invite} className="card invite-form" style={{ marginBottom: '1rem' }}>
        <div className="eyebrow invite-form-title">Zaproś pracownika</div>
        <div className="invite-field-email">
          <label className="form-label">E-mail pracownika</label>
          <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="pracownik@firma.pl" />
        </div>
        <div className="invite-field-name">
          <label className="form-label">Imię</label>
          <input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="invite-field-name">
          <label className="form-label">Nazwisko</label>
          <input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy || !email.trim()}>
          {busy ? 'Wysyłanie…' : 'Zaproś pracownika'}
        </button>
      </form>

      <form onSubmit={inviteCustomer} className="card invite-form" style={{ marginBottom: '1.5rem' }}>
        <div className="eyebrow invite-form-title">Zaproś klienta</div>
        <div className="invite-row-company">
          <div className="invite-field-company">
            <label className="form-label">Firma klienta</label>
            <select className="form-select" value={custNip} onChange={(e) => setCustNip(e.target.value)} required>
              <option value="">— wybierz firmę —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.nip}>{customerLabel(c)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="invite-field-email">
          <label className="form-label">E-mail klienta</label>
          <input className="form-input" type="email" value={custEmail} onChange={(e) => setCustEmail(e.target.value)} required placeholder="osoba@klient.pl" />
        </div>
        <div className="invite-field-name">
          <label className="form-label">Imię</label>
          <input className="form-input" value={custFirstName} onChange={(e) => setCustFirstName(e.target.value)} />
        </div>
        <div className="invite-field-name">
          <label className="form-label">Nazwisko</label>
          <input className="form-input" value={custLastName} onChange={(e) => setCustLastName(e.target.value)} />
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy || !custEmail.trim() || !custNip}>
          {busy ? 'Wysyłanie…' : 'Zaproś klienta'}
        </button>
      </form>

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : invitations.length === 0 ? (
        <div className="empty-state">Brak zaproszeń</div>
      ) : (
        <div className="card" style={{ padding: '0.5rem 0' }}>
          {invitations.map((inv) => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', flexWrap: 'wrap' }}>
              <span className={statusBadge[inv.status]}>{statusLabel[inv.status]}</span>
              <span className="mono invite-row-email" style={{ flex: 1, minWidth: 160 }}>{inv.email}</span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>
                {roleLabel(inv)}
              </span>
              {inv.status !== 'ACCEPTED' && (
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={rowBusyId === inv.id}
                    onClick={() => resend(inv)}
                    title="Wyśle nowy link aktywacyjny (stary przestaje działać)"
                  >
                    Ponów
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={rowBusyId === inv.id}
                    onClick={() => setConfirmCancel(inv)}
                  >
                    Anuluj
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmCancel && (
        <Modal
          title="Anuluj zaproszenie"
          onClose={() => { if (rowBusyId !== confirmCancel.id) setConfirmCancel(null); }}
          dismissable={rowBusyId !== confirmCancel.id}
        >
          <p style={{ marginBottom: '0.75rem', lineHeight: 1.6 }}>
            Czy na pewno anulować zaproszenie do <strong>{confirmCancel.email}</strong>?
            Link aktywacyjny przestanie działać.
          </p>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setConfirmCancel(null)}
              disabled={rowBusyId === confirmCancel.id}
            >
              Wróć
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={doCancel}
              disabled={rowBusyId === confirmCancel.id}
            >
              {rowBusyId === confirmCancel.id ? 'Anulowanie…' : 'Anuluj zaproszenie'}
            </button>
          </div>
        </Modal>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default TeamInvitations;
